import React from "react";
import { motion } from "framer-motion";
import {
  Server,
  Cpu,
  ShieldCheck,
  Layers3,
  Network,
  Workflow,
  Brain,
  Database,
  Globe,
  Eye,
  CircuitBoard,
  HardHat,
  TrendingUp,
  Calculator,
  Zap,
  Cloud,
  Lock,
  GitBranch,
  Activity,
  Search,
  FileText,
  ArrowRight,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Clock,
  Code,
  Boxes,
  KeyRound,
  Radar,
  BarChart3,
  Building2,
} from "lucide-react";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";

// ─────────────────────────────────────────────────────────────
// DATA
// ─────────────────────────────────────────────────────────────

const seams = [
  {
    num: "01",
    Icon: Workflow,
    title: "Function-calling contracts",
    color: "from-sky-500 to-cyan-400",
    blurb:
      "Every skill exports typed TOOL_DEFINITIONS. The LLM picks tools; the harness validates and dispatches. Skills swap without touching the model; models swap without touching the skills. This is the seam.",
  },
  {
    num: "02",
    Icon: Code,
    title: "Pydantic types — one source of truth",
    color: "from-violet-500 to-fuchsia-500",
    blurb:
      "One class becomes JSON Schema for the tool call, FastAPI request/response model, AND SQLAlchemy ORM mapping. Three roles, one declaration. Drift between LLM, API, and DB is structurally impossible.",
  },
  {
    num: "03",
    Icon: ShieldCheck,
    title: "The policy engine",
    color: "from-amber-500 to-orange-400",
    blurb:
      "Nothing crosses from \"the model said something\" to \"the enterprise did something\" without a policy_id and an audit row. Policies are first-class data, not code — written once, versioned, projected per stakeholder.",
  },
  {
    num: "04",
    Icon: Network,
    title: "Multi-model router",
    color: "from-emerald-500 to-teal-400",
    blurb:
      "Skill code never names a deployment. It calls resolve_deployment(domain, mode). GPT-4.1, GPT-5, Claude Opus/Sonnet, all Azure-hosted with private endpoints. Models become config; skills don't move.",
  },
];

const harnessPlanes = [
  {
    name: "Service plane",
    Icon: Server,
    color: "border-sky-200 bg-sky-50 dark:border-sky-900 dark:bg-sky-950/30",
    items: [
      "FastAPI ≥ 0.135 — one app, ~30 routers, mounted at root + /api for SWA forwarding",
      "Pydantic 2.12+ — universal I/O contract crossing LLM ↔ API ↔ DB",
      "SQLAlchemy 2.0 async + asyncpg + Alembic — ORM, driver, migrations (auto-run on App Service startup)",
      "PostgreSQL with pgvector — primary store + vector search for embeddings (powers the brain meta-skill)",
    ],
  },
  {
    name: "Reasoning plane",
    Icon: Cpu,
    color: "border-violet-200 bg-violet-50 dark:border-violet-900 dark:bg-violet-950/30",
    items: [
      "resolve_deployment(domain, mode) routes per task: nano for cheap classification, Sonnet for long reasoning, Opus for synthesis",
      "GPT function-calling as the universal tool surface (12 tools in chat; vision-policy tools in construction; etc.)",
      "Anthropic-via-Azure parallel path for Claude — same private tenant",
      "Tenacity retry + Azure content-filter sanitization + lazy-init clients to survive cold-start Key Vault races",
    ],
  },
  {
    name: "Governance plane",
    Icon: ShieldCheck,
    color: "border-amber-200 bg-amber-50 dark:border-amber-900 dark:bg-amber-950/30",
    items: [
      "Policy engine — every action references a policy_id with conditions, citations, thresholds, recommended action",
      "Real for OT DPI; partial for Vision/Construction; coded-not-data for Axiom's 16 policies (gap, see honest gaps)",
      "auth_audit_log for auth events; per-skill event tables for skill-level audit",
      "Phase 1 cookie + RS256 JWT + MFA (TOTP + recovery codes) + CSRF; Argon2id hashing; per-agent bearer for edge",
    ],
  },
  {
    name: "Interaction plane",
    Icon: Globe,
    color: "border-emerald-200 bg-emerald-50 dark:border-emerald-900 dark:bg-emerald-950/30",
    items: [
      "Single React/Tailwind shell at dev.eosai.ai — 76 pages, all skills mount here",
      "Chat surface — chat_router.py + Chat.jsx, 12-tool loop ≤5 iterations + SSE streaming",
      "Knowledge surface — Knowledge.jsx (Ask / Brief / Today / Ingest), proxies to total-recall-api",
      "Proactive scheduler (APScheduler in-process): Signals 3x daily, Vision compliance backfill at startup, CORR-004 startup trigger",
      "MJPEG streaming for construction cameras at a non-SWA path (SWA's 230s timeout would kill long streams)",
    ],
  },
  {
    name: "Cross-cutting",
    Icon: Layers3,
    color: "border-rose-200 bg-rose-50 dark:border-rose-900 dark:bg-rose-950/30",
    items: [
      "Azure-hosted in ot-sentry-rg (centralus) — App Service P1v3, Postgres Flex B2s, Redis Basic C0 (frame store), Key Vault, ACR, SWA with dev.eosai.ai custom domain",
      "Deploy: git push origin develop → GitHub Actions → ACR build → App Service swap. ~5 min end-to-end",
      "5 Azure Monitor alerts: 5xx rate, availability, Postgres CPU/storage, Key Vault failures",
      "Render is the legacy stack — frozen as fallback-only since 2026-04-28. Not in the active deploy path",
    ],
  },
];

const operationalSkills = [
  {
    domain: "Edge / OT / physical-world",
    items: [
      {
        name: "OT DPI Agent",
        Icon: Radar,
        blurb:
          "Deep packet inspection across 15+ industrial protocols (Modbus, DNP3, BACnet, SNMP, DICOM, HL7, S7, OPC UA, Profinet, IEC 61850), parsed to function-code level. Auto-discovery + role inference + risk scoring. Fuses CISA KEV + AlienVault OTX threat intel. Autonomous at the edge when WAN drops. Runs on Celerway 5G access-point hardware (sister-company OEM partner; Brian holds equity) in a hybrid topology — Docker container for north-south traffic, lightweight VM for east-west device-to-device lateral movement. East-west is the part Palo Alto's perimeter-only NGFW structurally cannot see. Every security event posts to Palo Alto Cortex XSOAR live today via a FastAPI background task — currently in dry-run (payload logged with DRYRUN- prefix, not sent; flipping to production is one env var). Critical events map to XSOAR Sev 1, warnings Sev 3, discovery Sev 4. Channel partnership with Palo Alto announced (SASE branded in our UI, Fed + SLG team meetings active); conversations now extending toward Palo acquiring this fat skill outright and licensing the harness underneath.",
      },
      {
        name: "Data Center Agent",
        Icon: Building2,
        blurb:
          "Two operating modes on one codebase: facilities-VLAN (UPS/PDU/CRAC/BMS classified by name+behavior, not IP) and out-of-band (BMC/iDRAC/iLO). Cross-segment correlation engine produces \"300 alarms, 1 top domino.\"",
      },
      {
        name: "Vision Agent",
        Icon: Eye,
        blurb:
          "YOLOv8 at the edge for PPE compliance, zone violations, worker safety, site progress. Redis-backed frame store for multi-worker scaling. Daily compliance reports auto-generated.",
      },
    ],
  },
  {
    domain: "Capital markets",
    items: [
      {
        name: "FMP Investment Model",
        Icon: TrendingUp,
        blurb:
          "Live signals screener 3x daily, factor-based portfolio construction. Validated in live trading; methodology is the source of the Vision Platform's 5-layer pattern.",
      },
      {
        name: "Back-test engine + Walk-forward testing",
        Icon: BarChart3,
        blurb:
          "Historical replay infrastructure with many strategy variants. Walk-forward is the rolling-window out-of-sample validation that promotes strategies. The discipline that separates a model that fits history from one that survives it.",
      },
    ],
  },
  {
    domain: "Domain intelligence",
    items: [
      {
        name: "Axiom",
        Icon: Boxes,
        blurb:
          "Governed corpus + 16-policy engine + 8-stakeholder process flows for AI-infrastructure development across site / build / commission / capital / exit. Owner's-rep flow is the most realized; other 7 personas are stub/partial. Proves the harness can host long-horizon institutional memory, not just real-time event triage.",
      },
    ],
  },
  {
    domain: "Cross-skill / decision support",
    items: [
      {
        name: "Chat",
        Icon: Workflow,
        blurb:
          "12 OT-DB query tools + the canonical multi-tool reasoning surface. Function-calling loop ≤5 iterations with SSE streaming.",
      },
      {
        name: "Construction Compliance / Briefings / FERC Intelligence",
        Icon: FileText,
        blurb:
          "Vision-policy enforcement per project/zone; proactive daily briefing generation (Axiom owner's-rep beachhead); FERC filings analysis. Briefings is the proof that proactive ≠ chat.",
      },
    ],
  },
  {
    domain: "Meta-skill",
    items: [
      {
        name: "Brain (Total Recall + gbrain)",
        Icon: Brain,
        blurb:
          "Institutional memory other skills compound through. Typed retrieval + synthesis tools (/v1/recall, /v1/brief, /v1/since, /v1/context, /v1/note, /v1/checkpoint) + Phase 6 write surface (/v1/ingest/*). Two-stakeholder projection: AI primary (via MCP) and human (via Knowledge UI). See Section 11 — this is the load-bearing substrate.",
      },
    ],
  },
];

const plannedSkills = [
  {
    name: "AHP decision tool",
    blurb:
      "Multi-agent Analytic Hierarchy Process panel. Designed in framework, no code yet. Brian's vision: a panel of agents each take a stakeholder role, score criteria pairwise, reconcile — disagreement stays visible and policy-traceable instead of being laundered out by a single LLM call.",
    status: "planned",
  },
  {
    name: "AIRMF (capital-markets)",
    blurb:
      "Six NLP agents (filings, news, earnings calls, regulatory, sentiment, policy disclosures). UI prototype only at /home/bfox/airmf; no Python backend yet.",
    status: "planned",
  },
  {
    name: "AI Governance / NIST RMF",
    blurb:
      "Recognized in archive (archive/ai-governance-pre-history-2025-05) as a real future skill shape; older 2025-era prototypes exist on disk but rebuild from scratch is the plan if/when commercially called. Do NOT try to salvage the pre-history code.",
    status: "archived",
  },
  {
    name: "USAspending data connector",
    blurb:
      "Named in the framework, NOT yet wired in code. Other adjacent connectors (EIA, NOAA, BLS, FMP, LBNL, USACE, FERC, GRIDSTATUS) are wired.",
    status: "planned",
  },
];

const surfaces = [
  {
    Icon: Lock,
    name: "AI Sentry",
    sub: "OT security",
    status: "Active customer engagements",
    statusColor: "emerald",
    blurb:
      "OT DPI Agent + Data Center Agent + Vision Agent + supporting skills, sold as the OT/ICS security platform on Celerway 5G access-point hardware (sister-company OEM). Palo Alto Networks channel partnership announced (Fed + State/Local); SASE branded in the AI Sentry UI; conversations now extending toward Palo acquiring this fat skill and licensing the harness underneath. The flagship surface — the one with revenue and the one most likely to dominate your first 90 days.",
  },
  {
    Icon: HardHat,
    name: "Axiom",
    sub: "DC construction intelligence",
    status: "Active, paired with AI Sentry",
    statusColor: "emerald",
    blurb:
      "Governed corpus + 16-policy engine + 8-stakeholder process flows for AI-infrastructure development. The skill that demonstrates the strategic shape: one corpus, one policy engine, many stakeholder process flows. Owner's-rep is the realized flow; other 7 personas are stub/partial.",
  },
  {
    Icon: TrendingUp,
    name: "Signals",
    sub: "Fintech / family-office",
    status: "Methodology-validated, latent commercial product",
    statusColor: "amber",
    blurb:
      "FMP investment model + back-test engine + walk-forward testing + (planned) AIRMF. Active personal use, validated in live trading. The methodology developed here became the Vision Platform's 5-layer pattern. Not commercialized — yet.",
  },
  {
    Icon: Eye,
    name: "Vision Platform",
    sub: "Multi-modal pattern intelligence",
    status: "Strategy locked, Phase 1 abstraction pending",
    statusColor: "amber",
    blurb:
      "Cross-domain methodology (5-layer pipeline + calibration + negative-example engineering + grid-search validation) abstracted from fmptrading; roadmapped to clinical / structural-biology / disaster / infrastructure / agriculture / industrial domains. TAM figures in the original brief are AI-generated and considered inflated — do not quote them as authoritative.",
  },
];

const azureResources = [
  { name: "App Service P1v3", role: "FastAPI app, 1-2 instances, scales" },
  { name: "Postgres Flex B2s", role: "Primary DB + pgvector + event tables + brain corpus" },
  { name: "Redis Basic C0", role: "Vision frame store, multi-worker shared" },
  { name: "Key Vault", role: "All secrets; bearer is recall-api-bearer (NOT -token)" },
  { name: "ACR (otsentryacr.azurecr.io)", role: "Container registry, ACR build pipeline" },
  { name: "Static Web App", role: "React shell with dev.eosai.ai custom domain" },
  { name: "Resource group: ot-sentry-rg", role: "centralus region, private LuxML tenant" },
];

const operatingRules = [
  {
    rule: "Deterministic queries.",
    why: "The brain is structured so the same question gets the same authoritative answer every session. If a query returns a thin or wrong answer, the fix is to amend the corpus, not to handle it in the conversation. This is the \"lower Brian's cycles\" property.",
  },
  {
    rule: "Trust lifecycle: current over lifecycle: archived.",
    why: "Synthesis prompts already weight current pages. Archived pages are valid for \"what was the prior reasoning\" — never for \"what's true right now.\"",
  },
  {
    rule: "TAM figures from the Vision Platform brief are AI-generated and considered inflated.",
    why: "Brian explicitly flagged. Do not quote them as fact. The page itself carries a tam_caveat field — honor it.",
  },
  {
    rule: "No customer-specific data goes into the corpus.",
    why: "Deal sizes, named customer commitments, pipeline status, opportunity dollars — none of this is in the brain by design. If you need it for a meeting, ask Brian directly. Do not invent it.",
  },
  {
    rule: "Don't pretend planned skills are operational.",
    why: "AHP, AIRMF six-NLP-agents, AI Governance/NIST RMF, USAspending connector. Mark them status: planned or status: archived when discussing.",
  },
  {
    rule: "Forge is a sister project, not an eosai skill.",
    why: "Lives in /home/bfox/forge-runtime with its own Azure deployment and customer-portable design. coding_router.py and grok_router.py in ot-sentry are dormant residuals — don't extend them.",
  },
  {
    rule: "Per-repo logging discipline.",
    why: "When work spans repos, update each repo's CLAUDE log. total-recall/CLAUDE_SESSION_LOG.md and ot-sentry/CLAUDE_LOG.md are the two main ones. Brain timeline entry is also expected on material work.",
  },
  {
    rule: "Production hasn't been built yet (decision: park).",
    why: "dev.eosai.ai IS the live platform. The Azure prod App Service is provisioned but Stopped. Don't propose customer-prod work without checking parking-lot/production-branch-plan-2026-05-05 — it explains the three scopes and Brian's call to defer.",
  },
  {
    rule: "Two key environment variable gotchas.",
    why: "Key Vault secret is recall-api-bearer (NOT recall-api-token despite older docs). INGEST_REPOS supports a 3-field format name|url|branch — ot-sentry uses develop, forge-runtime uses main.",
  },
  {
    rule: "When in doubt, write what you don't know in the answer.",
    why: "Brian values \"I don't have visibility into X; you'll want to source that separately\" over a confident fabrication. Honest gaps are useful; invented certainty is dangerous.",
  },
];

const honestGaps = [
  {
    name: "Per-tenant data isolation is partial",
    detail: "tenant_id is nullable on most rows. Real isolation is a 4-6d engineering item required before SSO and before serving regulated multi-tenant customers.",
  },
  {
    name: "Policy engine coverage is uneven",
    detail: "Real for OT DPI (PolicyDB → policy_node_assignment → EventDB.policy_id audit chain). Code-not-data for Axiom's 16 policies. Minimal for FMP, Procurement, Coding.",
  },
  {
    name: "Cross-skill function-call composition is unproven",
    detail: "Mechanically possible (shared Pydantic types + function-calling); not currently exercised in production. A demo that uses the OT agent and a back-tester in one reasoning chain would prove this real.",
  },
  {
    name: "auto_links and auto_timeline disabled on gbrain",
    detail: "Brain link graph is essentially flat — graph traversal queries return little. Re-enabling these is worth a sprint at some point.",
  },
  {
    name: "No SOC 2 yet",
    detail: "Listed as months 3-6 enterprise-readiness work. SSO/OIDC is also parked.",
  },
  {
    name: "Test coverage is uneven",
    detail: "Forge has 48 tests at 97% coverage with 90% threshold enforced — that's the bar to mirror back into ot-sentry. ot-sentry's coverage is currently lower.",
  },
  {
    name: "Corpus content is partially stale",
    detail: "2026-05-04 lifecycle audit tagged 30 pages current and 115 archived. Brain answers weight current over archived; if current conflicts with archived, current wins.",
  },
  {
    name: "Auto-ingest worker has historically broken silently",
    detail: "The worker that polls 11 repos and writes commit summaries as timeline entries has had multi-bug repair cycles. If timelines stop updating, that's the first thing to check.",
  },
];

const durableEdges = [
  { name: "Calibration methodology", why: "Annotated ground truth + iterative prompt engineering + silent-failure detection — learned, not purchased. Off-the-shelf Vision gets 70-75%; we have a documented path to 86-93%." },
  { name: "Negative-example engineering", why: "Most implementations optimize positive recall; we have a documented process for not-X precision. False-positive suppression is the primary barrier to clinical/regulatory adoption." },
  { name: "Multi-scale concordance", why: "Architectural commitment, not just an algorithm. Reduces FPR without proportional loss of TPR." },
  { name: "Grid-search validation", why: "240-combination parameter sweeps with pre-computed pipelines produce statistically robust optima — regulatory-submission grade." },
  { name: "Corpus that compounds", why: "Every event/decision/outcome lands in one temporal store. Each skill benefits from data prior skills generated. Single-purpose AI products structurally cannot reach this network effect." },
  { name: "Stakeholder views as policy projections", why: "Adding a new persona is a config of the policy engine, not a new product. Realized for Axiom (8 personas) — same property makes eosai horizontal across domains." },
];

const dataConnectors = [
  { source: "EIA", provides: "Grid capacity, generation, interconnect-queue, load forecasts", status: "Wired" },
  { source: "NOAA", provides: "Climate, weather, severe-weather risk overlays", status: "Wired" },
  { source: "BLS", provides: "Construction labor availability, wage indices, regional employment", status: "Wired" },
  { source: "FMP", provides: "Equities, fundamentals, ratios, market data", status: "Wired" },
  { source: "USAspending", provides: "Federal awards, permits, contract trails", status: "Named, not wired" },
  { source: "LBNL queue / USACE / FERC / ISO queue / GRIDSTATUS", provides: "Adjacent infrastructure feeds — interconnection queues, permits, grid markets", status: "Wired" },
];

const first30Days = [
  {
    week: "Week 1",
    title: "Orient with the brain",
    items: [
      "Read the hub page: gbrain get anchors/eosai-framework — corpus map, ~5 min",
      "Read ot-sentry-repo/deployment-topology — canonical \"where does this run\"",
      "Read archive/project_brain_repair_punch_list_2026-05-04 — what's outstanding",
      "List parking-lot/* — deferred decisions, don't re-litigate",
      "Read this page top-to-bottom one more time with the brain pages open in another tab",
    ],
  },
  {
    week: "Week 2",
    title: "Match Brian's dev environment",
    items: [
      "Clone the 11 active repos under ~/projects — ot-sentry, total-recall, forge-runtime, fmptrading, plus 7 more (see brain page projects/)",
      "Wire MCP gbrain tools in your Claude Code config — or install gbrain CLI",
      "Set up Azure CLI access (private LuxML tenant), Vercel CLI, GitHub CLI",
      "Run ot-sentry locally: cd backend && bash scripts/run_dev.sh — port 8001, dev DB on localhost:5434",
      "Push a trivial commit to develop and watch GitHub Actions → ACR → App Service swap end-to-end",
    ],
  },
  {
    week: "Week 3",
    title: "Pair on one skill end-to-end",
    items: [
      "Brian picks the skill (likely OT DPI Agent or Axiom owner's-rep flow)",
      "Trace one event from edge → router → policy_id → audit row → UI",
      "Read the relevant skill page in the brain (skills/<name>) — confirm it matches the code",
      "If it doesn't, you've found your first concrete corpus update — write it",
    ],
  },
  {
    week: "Week 4",
    title: "Make yourself part of the system",
    items: [
      "Add anchors/people/<your-name> to the brain — Brian's intro prompts will surface it to future agents",
      "Add anchors/people/<your-name>-operating-mode — how you like to collaborate, your hard rules",
      "Write your own brain_startup_prompt.md addendum — what you want fresh agents to know about your style",
      "Take ownership of one Tier 2 punch-list item end-to-end",
      "Update CLAUDE_LOG.md in every repo you touched this month with what you learned",
    ],
  },
];

const brainSurfaces = [
  {
    Icon: Brain,
    title: "MCP tools (preferred, Claude Code in a terminal)",
    detail:
      "mcp__gbrain__search, mcp__gbrain__get_page, mcp__gbrain__put_page, mcp__gbrain__query, mcp__gbrain__get_timeline. Direct, typed, zero-friction. This is how I (the AI agent writing this page) reach the brain right now.",
  },
  {
    Icon: Code,
    title: "gbrain CLI (shell-friendly, equivalent)",
    detail:
      "gbrain get <slug>, gbrain search <query>, gbrain query <semantic-question>, gbrain list --slug-prefix <ns>/. Hits the same Azure Postgres backend. Useful when MCP isn't wired or you're scripting.",
  },
  {
    Icon: Cloud,
    title: "HTTP endpoints (any LLM, any harness)",
    detail:
      "POST /v1/recall, /v1/brief, /v1/since, /v1/context, /v1/note, /v1/checkpoint. Bearer auth via Key Vault secret recall-api-bearer. The path for ChatGPT, web Claude, or a curl-based briefer.",
  },
  {
    Icon: Globe,
    title: "Human UI",
    detail:
      "https://dev.eosai.ai/knowledge — Ask / Brief / Today / Ingest tabs. Same backend as MCP. Brian uses this to paste questions on behalf of an LLM that has no shell, or to ingest new pages directly.",
  },
];

// ─────────────────────────────────────────────────────────────
// SUB-COMPONENTS
// ─────────────────────────────────────────────────────────────

function SectionLabel({ index, children }) {
  return (
    <p className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-400 mb-2">
      {String(index).padStart(2, "0")} / {children}
    </p>
  );
}

function StatusBadge({ status }) {
  const colors = {
    operational: "border-emerald-300 bg-emerald-50 text-emerald-700 dark:border-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300",
    planned: "border-amber-300 bg-amber-50 text-amber-700 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-300",
    archived: "border-zinc-300 bg-zinc-50 text-zinc-600 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-400",
    emerald: "border-emerald-300 bg-emerald-50 text-emerald-700 dark:border-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300",
    amber: "border-amber-300 bg-amber-50 text-amber-700 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-300",
  };
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${colors[status] || colors.operational}`}>
      {status}
    </span>
  );
}

// ─────────────────────────────────────────────────────────────
// MAIN
// ─────────────────────────────────────────────────────────────

export default function EosaiStory() {
  return (
    <div className="min-h-screen bg-gradient-to-b from-zinc-50 via-white to-zinc-100 text-zinc-900 dark:from-zinc-950 dark:via-zinc-950 dark:to-zinc-900 dark:text-zinc-100">
      <div className="mx-auto max-w-7xl px-6 pb-24 lg:px-10">

        {/* ── #1 PRE-HERO HOOK ─────────────────────────────────── */}
        <section className="pt-10 lg:pt-16">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
          >
            <Card className="rounded-[24px] border-0 bg-zinc-900 text-white shadow-2xl">
              <CardContent className="px-8 py-6 lg:px-10">
                <p className="text-lg leading-8 text-zinc-200 italic">
                  "If you take this job, you're not inheriting an AI application. You're inheriting a thesis — that the harness should be thin, the skills should be fat, and a brain should brief every agent the moment they open a session. Everything else follows from that."
                </p>
                <p className="mt-3 text-sm font-semibold text-zinc-500">
                  Read this end-to-end. It is exhaustive on purpose.
                </p>
              </CardContent>
            </Card>
          </motion.div>
        </section>

        {/* ── #2 HERO ──────────────────────────────────────────── */}
        <section className="py-14 lg:py-20">
          <div className="grid gap-10 lg:grid-cols-[1.2fr_0.8fr] lg:items-end">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
              <h1 className="text-5xl font-bold leading-[1.1] tracking-tight sm:text-6xl lg:text-7xl">
                <span className="whitespace-nowrap">Everything you'll</span>{" "}
                <span className="text-zinc-400">inherit.</span>
                <br />
                <span className="whitespace-nowrap">In one read.</span>
              </h1>
              <p className="mt-6 max-w-xl text-lg leading-8 text-zinc-500 dark:text-zinc-400">
                Four product surfaces (AI Sentry, Axiom, Signals, Vision Platform) on one substrate.
                A thin harness inherited by every skill. A brain that makes AI agents pick up where
                the last session left off. <span className="font-semibold text-zinc-700 dark:text-zinc-200">dev.eosai.ai</span> is
                the live proof, running today on Azure. This page is the playbook for taking it over.
              </p>
              <div className="mt-8 flex flex-wrap items-center gap-3">
                {["Thin harness", "Fat skills", "Four seams", "One corpus", "A brain"].map((step, i, arr) => (
                  <div key={step} className="flex items-center gap-3">
                    <div className="rounded-full border bg-white px-4 py-2 text-sm font-medium shadow-sm dark:bg-zinc-900">{step}</div>
                    {i < arr.length - 1 && <ArrowRight className="h-4 w-4 text-zinc-400" />}
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.15 }}>
              <Card className="rounded-[28px] border-0 bg-zinc-900 text-white shadow-2xl">
                <CardContent className="p-8">
                  <p className="mb-4 text-xs font-semibold uppercase tracking-[0.2em] text-zinc-400">Core thesis</p>
                  <p className="mb-4 text-sm leading-7 text-zinc-300">
                    Most enterprise AI is built upside-down: thick bespoke logic with auth, audit,
                    policy, retrieval, and UX bolted on. Every new use case re-implements the same
                    plumbing badly.
                  </p>
                  <p className="mb-4 text-sm leading-7 text-zinc-300">
                    eosai inverts it. The <span className="font-semibold text-white">harness</span> is generic,
                    thin, and shared. The <span className="font-semibold text-white">skills</span> are
                    domain-deep and self-contained. Four seams hold them together. One corpus compounds across them.
                  </p>
                  <p className="text-sm leading-7 text-zinc-300">
                    Our job is not to write more applications. It's to grow more skills, keep the
                    harness honest, and feed the brain so the next agent on the keyboard — human or
                    model — doesn't start from zero.
                  </p>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </section>

        {/* ── #3 CREDIBILITY STRIP ─────────────────────────────── */}
        <section className="border-y border-zinc-200 py-5 dark:border-zinc-800">
          <div className="grid gap-3 text-center sm:grid-cols-2 lg:grid-cols-5">
            {[
              { value: "LIVE", label: "dev.eosai.ai on Azure" },
              { value: "4", label: "Strategic surfaces" },
              { value: "11", label: "Active repos auto-ingested" },
              { value: "Palo Alto", label: "Channel partnership (Fed + SLG)" },
              { value: "Parked", label: "Production by deliberate choice" },
            ].map((s) => (
              <div key={s.label} className="px-2">
                <div className="text-xl font-bold tracking-tight text-zinc-800 dark:text-zinc-100">{s.value}</div>
                <div className="mt-0.5 text-[11px] uppercase tracking-wider text-zinc-500">{s.label}</div>
              </div>
            ))}
          </div>
        </section>

        {/* ── #4 THE FOUR SEAMS ────────────────────────────────── */}
        <section className="py-12 lg:py-16">
          <div className="mb-10">
            <SectionLabel index={1}>The four seams</SectionLabel>
            <h2 className="text-3xl font-bold tracking-tight">The joints that make composition real.</h2>
            <p className="mt-3 max-w-2xl text-base leading-7 text-zinc-500 dark:text-zinc-400">
              These are the architectural commitments that turn "thin harness + fat skills" from
              a slogan into something that actually composes. If you remember one thing about the
              architecture, remember these four.
            </p>
          </div>
          <div className="grid gap-5 sm:grid-cols-2">
            {seams.map((s) => (
              <Card key={s.title} className="rounded-[24px] border-2 border-zinc-100 bg-white shadow-md dark:border-zinc-800 dark:bg-zinc-900/80">
                <CardContent className="p-6">
                  <div className="mb-4 flex items-center gap-4">
                    <div className={`flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br ${s.color} text-white shadow-lg`}>
                      <s.Icon className="h-5 w-5" />
                    </div>
                    <div>
                      <div className="text-xs font-semibold uppercase tracking-[0.18em] text-zinc-400">Seam {s.num}</div>
                      <div className="text-base font-bold tracking-tight">{s.title}</div>
                    </div>
                  </div>
                  <p className="text-sm leading-7 text-zinc-600 dark:text-zinc-400">{s.blurb}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        {/* ── #5 THE HARNESS, LAYER BY LAYER ───────────────────── */}
        <section className="py-12 lg:py-16">
          <div className="mb-10">
            <SectionLabel index={2}>The harness</SectionLabel>
            <h2 className="text-3xl font-bold tracking-tight">Thin. Generic. Shared. Inherited by every skill.</h2>
            <p className="mt-3 max-w-2xl text-base leading-7 text-zinc-500 dark:text-zinc-400">
              None of this is per-skill. None of this should be rewritten. If a skill is reaching
              outside the harness for something the harness should provide, the answer is to fix
              the harness — not to bypass it.
            </p>
          </div>
          <div className="space-y-4">
            {harnessPlanes.map((plane) => (
              <Card key={plane.name} className={`rounded-[20px] border-2 ${plane.color}`}>
                <CardContent className="p-6">
                  <div className="mb-4 flex items-center gap-3">
                    <div className="rounded-xl bg-zinc-900 p-2 text-white dark:bg-zinc-100 dark:text-zinc-900">
                      <plane.Icon className="h-5 w-5" />
                    </div>
                    <h3 className="text-lg font-bold tracking-tight">{plane.name}</h3>
                  </div>
                  <ul className="space-y-2">
                    {plane.items.map((item, i) => (
                      <li key={i} className="flex gap-2 text-sm leading-7 text-zinc-700 dark:text-zinc-300">
                        <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-zinc-400" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        {/* ── #6 SKILLS INVENTORY ──────────────────────────────── */}
        <section className="py-12 lg:py-16">
          <div className="mb-10">
            <SectionLabel index={3}>The skills</SectionLabel>
            <h2 className="text-3xl font-bold tracking-tight">Fat. Domain-deep. Deliberately heterogeneous.</h2>
            <p className="mt-3 max-w-2xl text-base leading-7 text-zinc-500 dark:text-zinc-400">
              An OT packet inspector and a walk-forward back-tester have nothing in common at the
              domain level. They share <em>everything</em> at the harness level. That is the entire
              point. Status badges are accurate. <span className="font-semibold">Do not promise planned skills as operational.</span>
            </p>
          </div>

          <div className="space-y-8">
            {operationalSkills.map((group) => (
              <div key={group.domain}>
                <div className="mb-4 flex items-center gap-3">
                  <h3 className="text-sm font-bold uppercase tracking-[0.15em] text-zinc-500">{group.domain}</h3>
                  <StatusBadge status="operational" />
                </div>
                <div className="grid gap-4 lg:grid-cols-2">
                  {group.items.map((skill) => (
                    <Card key={skill.name} className="rounded-[20px] border border-zinc-200 bg-white dark:border-zinc-800 dark:bg-zinc-900/70">
                      <CardContent className="p-6">
                        <div className="mb-3 flex items-center gap-3">
                          <div className="rounded-xl bg-zinc-100 p-2 text-zinc-700 dark:bg-zinc-800 dark:text-zinc-200">
                            <skill.Icon className="h-4 w-4" />
                          </div>
                          <div className="text-base font-bold tracking-tight">{skill.name}</div>
                        </div>
                        <p className="text-sm leading-7 text-zinc-600 dark:text-zinc-400">{skill.blurb}</p>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            ))}

            <div>
              <div className="mb-4 flex items-center gap-3">
                <h3 className="text-sm font-bold uppercase tracking-[0.15em] text-zinc-500">Framework-named, not yet built</h3>
                <StatusBadge status="planned" />
              </div>
              <div className="grid gap-4 lg:grid-cols-2">
                {plannedSkills.map((skill) => (
                  <Card key={skill.name} className="rounded-[20px] border border-amber-200 bg-amber-50/40 dark:border-amber-900 dark:bg-amber-950/20">
                    <CardContent className="p-6">
                      <div className="mb-2 flex items-center justify-between">
                        <div className="text-base font-bold tracking-tight">{skill.name}</div>
                        <StatusBadge status={skill.status} />
                      </div>
                      <p className="text-sm leading-7 text-zinc-700 dark:text-zinc-300">{skill.blurb}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ── #7 THE FOUR STRATEGIC SURFACES ───────────────────── */}
        <section className="py-12 lg:py-16">
          <div className="mb-10">
            <SectionLabel index={4}>Strategic surfaces</SectionLabel>
            <h2 className="text-3xl font-bold tracking-tight">Four products. One substrate.</h2>
            <p className="mt-3 max-w-2xl text-base leading-7 text-zinc-500 dark:text-zinc-400">
              These are the LuxML product portfolio surfaces that go to market. The first three are
              operational; the fourth is methodology-validated and roadmap-staged. Each is a
              projection of the same harness through different policies and skills.
            </p>
          </div>
          <div className="grid gap-6 lg:grid-cols-2">
            {surfaces.map((surf) => (
              <Card key={surf.name} className="rounded-[24px] border-2 border-zinc-100 bg-white shadow-md dark:border-zinc-800 dark:bg-zinc-900/80">
                <CardContent className="p-7">
                  <div className="mb-4 flex items-start justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <div className="rounded-2xl bg-zinc-900 p-2.5 text-white dark:bg-zinc-100 dark:text-zinc-900">
                        <surf.Icon className="h-5 w-5" />
                      </div>
                      <div>
                        <div className="text-xl font-bold tracking-tight">{surf.name}</div>
                        <div className="text-xs uppercase tracking-[0.15em] text-zinc-400">{surf.sub}</div>
                      </div>
                    </div>
                    <StatusBadge status={surf.statusColor} />
                  </div>
                  <p className="text-sm leading-7 text-zinc-600 dark:text-zinc-400">{surf.blurb}</p>
                  <div className="mt-4 text-xs font-semibold uppercase tracking-wider text-zinc-500">{surf.status}</div>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        {/* ── #8 SISTER PROJECT — FORGE ────────────────────────── */}
        <section className="py-12 lg:py-16">
          <div className="mb-8">
            <SectionLabel index={5}>Sister project</SectionLabel>
            <h2 className="text-3xl font-bold tracking-tight">Forge is not a fifth surface.</h2>
          </div>
          <Card className="rounded-[24px] border-2 border-zinc-100 bg-white shadow-md dark:border-zinc-800 dark:bg-zinc-900/80">
            <CardContent className="p-7">
              <div className="mb-4 flex items-center gap-3">
                <div className="rounded-xl bg-violet-100 p-2 text-violet-700 dark:bg-violet-950/40 dark:text-violet-300">
                  <GitBranch className="h-5 w-5" />
                </div>
                <div>
                  <div className="text-lg font-bold tracking-tight">Forge — customer-portable coding terminal</div>
                  <div className="text-xs uppercase tracking-[0.15em] text-zinc-400">forge-runtime-api.azurewebsites.net</div>
                </div>
              </div>
              <p className="mb-3 text-sm leading-7 text-zinc-600 dark:text-zinc-400">
                Independent codebase at <code className="rounded bg-zinc-100 px-1.5 py-0.5 text-xs dark:bg-zinc-800">/home/bfox/forge-runtime</code>.
                Separate Azure resource group, separate auth/CI/deploy. Designed to port into a
                customer's own Azure tenant when an engagement needs AI inside their perimeter
                rather than ours. Brian uses it daily.
              </p>
              <div className="mt-4 rounded-2xl border border-rose-200 bg-rose-50/60 p-4 dark:border-rose-900 dark:bg-rose-950/20">
                <div className="mb-1 flex items-center gap-2 text-xs font-bold uppercase tracking-[0.15em] text-rose-700 dark:text-rose-300">
                  <AlertTriangle className="h-3.5 w-3.5" /> Operating rule
                </div>
                <p className="text-sm leading-7 text-rose-900 dark:text-rose-200">
                  <code className="rounded bg-rose-100 px-1.5 py-0.5 text-xs dark:bg-rose-950">coding_router.py</code> and{" "}
                  <code className="rounded bg-rose-100 px-1.5 py-0.5 text-xs dark:bg-rose-950">grok_router.py</code> in
                  ot-sentry are dormant residuals from before Forge was extracted. Don't extend them
                  — anything coding-agent-shaped belongs in the Forge repo, not eosai.
                </p>
              </div>
              <p className="mt-4 text-sm leading-7 text-zinc-600 dark:text-zinc-400">
                Forge also has the best test discipline in the family — 48 tests at 97% coverage
                with a 90% threshold enforced in CI. That's the bar to mirror back into ot-sentry.
              </p>
            </CardContent>
          </Card>
        </section>

        {/* ── #9 WHERE IT RUNS ─────────────────────────────────── */}
        <section className="py-12 lg:py-16">
          <div className="mb-10">
            <SectionLabel index={6}>Deployment</SectionLabel>
            <h2 className="text-3xl font-bold tracking-tight">Where it actually runs.</h2>
            <p className="mt-3 max-w-2xl text-base leading-7 text-zinc-500 dark:text-zinc-400">
              Azure is the primary. Render is the legacy stack, frozen as fallback only. dev.eosai.ai
              IS the live platform. Production is provisioned but parked deliberately.
            </p>
          </div>

          <div className="grid gap-6 lg:grid-cols-[1.3fr_0.7fr]">
            <Card className="rounded-[24px] border-2 border-sky-200 bg-sky-50/40 dark:border-sky-900 dark:bg-sky-950/20">
              <CardContent className="p-7">
                <div className="mb-4 flex items-center gap-3">
                  <div className="rounded-xl bg-sky-600 p-2 text-white">
                    <Cloud className="h-5 w-5" />
                  </div>
                  <h3 className="text-lg font-bold tracking-tight">Azure (primary, live)</h3>
                </div>
                <div className="space-y-2">
                  {azureResources.map((r) => (
                    <div key={r.name} className="flex flex-col gap-1 rounded-xl border border-sky-200 bg-white/80 p-3 sm:flex-row sm:items-baseline sm:gap-4 dark:border-sky-900 dark:bg-zinc-900/40">
                      <div className="min-w-[14rem] text-sm font-semibold text-zinc-800 dark:text-zinc-100">{r.name}</div>
                      <div className="text-xs text-zinc-600 dark:text-zinc-400">{r.role}</div>
                    </div>
                  ))}
                </div>
                <div className="mt-5 rounded-2xl bg-zinc-900 p-5 text-white">
                  <p className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-400">Deploy pipeline</p>
                  <p className="mt-2 text-sm leading-7 text-zinc-200">
                    <code className="rounded bg-zinc-800 px-1.5 py-0.5 text-xs">git push origin develop</code> →
                    GitHub Actions → ACR build → App Service swap. ~5 min end-to-end. Alembic
                    migrations auto-run on App Service startup.
                  </p>
                </div>
              </CardContent>
            </Card>

            <div className="space-y-5">
              <Card className="rounded-[20px] border-2 border-zinc-200 bg-zinc-50/60 dark:border-zinc-800 dark:bg-zinc-900/40">
                <CardContent className="p-6">
                  <div className="mb-3 flex items-center gap-2 text-xs font-bold uppercase tracking-[0.15em] text-zinc-500">
                    <XCircle className="h-3.5 w-3.5" /> Render (frozen)
                  </div>
                  <p className="text-sm leading-7 text-zinc-700 dark:text-zinc-300">
                    Legacy stack. Frozen as fallback-only since 2026-04-28. Not in active deploy
                    path. Two backends + two frontends still running for instant rollback insurance.
                    <span className="font-semibold"> Do nothing to Render.</span> All work ships to Azure.
                  </p>
                </CardContent>
              </Card>
              <Card className="rounded-[20px] border-2 border-amber-200 bg-amber-50/60 dark:border-amber-900 dark:bg-amber-950/20">
                <CardContent className="p-6">
                  <div className="mb-3 flex items-center gap-2 text-xs font-bold uppercase tracking-[0.15em] text-amber-700 dark:text-amber-300">
                    <Clock className="h-3.5 w-3.5" /> Production (parked)
                  </div>
                  <p className="text-sm leading-7 text-zinc-700 dark:text-zinc-300">
                    The Azure prod App Service is provisioned but Stopped. Brian's locked
                    decision: defer until a real customer driver shows up. See brain page{" "}
                    <code className="rounded bg-amber-100 px-1.5 py-0.5 text-xs dark:bg-amber-950">parking-lot/production-branch-plan-2026-05-05</code>.
                    Don't propose prod work without checking it first.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* ── #10 THE BRAIN — YOUR OPERATING PARTNER ───────────── */}
        <section className="py-12 lg:py-16">
          <div className="mb-10 grid gap-6 lg:grid-cols-[1fr_auto] lg:items-end">
            <div>
              <SectionLabel index={7}>The brain</SectionLabel>
              <h2 className="text-3xl font-bold tracking-tight">Total Recall + gbrain — the substrate that makes a digital coworker possible.</h2>
              <p className="mt-3 max-w-3xl text-base leading-7 text-zinc-500 dark:text-zinc-400">
                This section is the most important one for understanding how this place actually
                runs. The brain is what lets AI agents pick up where the last session left off,
                across humans and across models. Brian uses it as a tool for a digital user —
                literally for the AI agent reading the room. You'll use it the same way.
              </p>
            </div>
            <Badge className="rounded-full px-4 py-1.5 text-xs font-semibold">Read this twice</Badge>
          </div>

          <div className="grid gap-6 lg:grid-cols-[1.1fr_0.9fr]">
            <Card className="rounded-[24px] border-0 bg-zinc-900 text-white shadow-2xl">
              <CardContent className="p-8">
                <div className="mb-5 flex items-center gap-3">
                  <div className="rounded-2xl bg-gradient-to-br from-violet-500 to-fuchsia-500 p-2.5 text-white">
                    <Brain className="h-5 w-5" />
                  </div>
                  <div>
                    <div className="text-xs font-semibold uppercase tracking-[0.18em] text-zinc-400">Architecture</div>
                    <div className="text-lg font-bold tracking-tight">What it is</div>
                  </div>
                </div>
                <p className="mb-4 text-sm leading-7 text-zinc-300">
                  <span className="font-semibold text-white">Total Recall</span> is a FastAPI service
                  at <code className="rounded bg-zinc-800 px-1.5 py-0.5 text-xs">total-recall-api.azurewebsites.net</code> that
                  exposes typed retrieval + synthesis endpoints (/v1/recall, /v1/brief, /v1/since,
                  /v1/context, /v1/note, /v1/checkpoint, /v1/ingest/*).
                </p>
                <p className="mb-4 text-sm leading-7 text-zinc-300">
                  <span className="font-semibold text-white">gbrain</span> is the underlying knowledge
                  graph — Postgres + pgvector at <code className="rounded bg-zinc-800 px-1.5 py-0.5 text-xs">total-recall-db.postgres.database.azure.com</code>,
                  with an HTTP MCP front-end at <code className="rounded bg-zinc-800 px-1.5 py-0.5 text-xs">recall-gbrain.azurewebsites.net</code>.
                </p>
                <p className="text-sm leading-7 text-zinc-300">
                  The <span className="font-semibold text-white">corpus</span> is ~200 pages
                  organized by namespace (<code className="rounded bg-zinc-800 px-1.5 py-0.5 text-xs">anchors/</code>,{" "}
                  <code className="rounded bg-zinc-800 px-1.5 py-0.5 text-xs">harness/</code>,{" "}
                  <code className="rounded bg-zinc-800 px-1.5 py-0.5 text-xs">seams/</code>,{" "}
                  <code className="rounded bg-zinc-800 px-1.5 py-0.5 text-xs">skills/</code>,{" "}
                  <code className="rounded bg-zinc-800 px-1.5 py-0.5 text-xs">data-sources/</code>,{" "}
                  <code className="rounded bg-zinc-800 px-1.5 py-0.5 text-xs">parking-lot/</code>,{" "}
                  <code className="rounded bg-zinc-800 px-1.5 py-0.5 text-xs">archive/</code>). Every page has frontmatter
                  including <code className="rounded bg-zinc-800 px-1.5 py-0.5 text-xs">lifecycle: current</code> or{" "}
                  <code className="rounded bg-zinc-800 px-1.5 py-0.5 text-xs">lifecycle: archived</code>.
                </p>
              </CardContent>
            </Card>

            <Card className="rounded-[24px] border-2 border-violet-200 bg-violet-50/40 dark:border-violet-900 dark:bg-violet-950/20">
              <CardContent className="p-7">
                <div className="mb-4 text-xs font-bold uppercase tracking-[0.15em] text-violet-700 dark:text-violet-300">Why it exists</div>
                <p className="mb-3 text-sm leading-7 text-zinc-700 dark:text-zinc-300">
                  Without the brain, every fresh session of every AI agent — Claude Code, web Claude,
                  ChatGPT, a deferred batch agent — starts from zero. Brian briefs them, they make
                  progress, the session ends, the context evaporates.
                </p>
                <p className="mb-3 text-sm leading-7 text-zinc-700 dark:text-zinc-300">
                  With the brain, the agent reads the hub page first (<code className="rounded bg-violet-100 px-1.5 py-0.5 text-xs dark:bg-violet-950">anchors/eosai-framework</code>),
                  the deployment topology, and whatever else is load-bearing for the task. Five
                  minutes of structured retrieval saves an hour of stumbling. Same question gets the
                  same authoritative answer every time.
                </p>
                <p className="text-sm leading-7 text-zinc-700 dark:text-zinc-300">
                  That's the property Brian calls "lower his cycles." It is the entire reason the
                  brain exists. If a query returns a thin or wrong answer, you fix it by amending
                  the corpus — not by repeating yourself in the next session.
                </p>
              </CardContent>
            </Card>
          </div>

          {/* How agents reach it */}
          <div className="mt-8">
            <h3 className="mb-4 text-lg font-bold tracking-tight">How an AI agent reaches the brain</h3>
            <div className="grid gap-4 sm:grid-cols-2">
              {brainSurfaces.map((s) => (
                <Card key={s.title} className="rounded-[20px] border border-zinc-200 bg-white dark:border-zinc-800 dark:bg-zinc-900/70">
                  <CardContent className="p-6">
                    <div className="mb-3 flex items-center gap-3">
                      <div className="rounded-xl bg-zinc-100 p-2 text-zinc-700 dark:bg-zinc-800 dark:text-zinc-200">
                        <s.Icon className="h-4 w-4" />
                      </div>
                      <div className="text-sm font-bold tracking-tight">{s.title}</div>
                    </div>
                    <p className="text-sm leading-7 text-zinc-600 dark:text-zinc-400">{s.detail}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* How Brian uses it */}
          <div className="mt-8">
            <Card className="rounded-[24px] border-2 border-emerald-200 bg-emerald-50/40 dark:border-emerald-900 dark:bg-emerald-950/20">
              <CardContent className="p-7">
                <div className="mb-4 flex items-center gap-3">
                  <div className="rounded-xl bg-emerald-600 p-2 text-white">
                    <KeyRound className="h-5 w-5" />
                  </div>
                  <h3 className="text-lg font-bold tracking-tight">How Brian briefs an AI agent at session start (this is the pattern to copy)</h3>
                </div>
                <p className="mb-3 text-sm leading-7 text-zinc-700 dark:text-zinc-300">
                  Every time Brian opens a Claude Code session, he pastes three documents from{" "}
                  <code className="rounded bg-emerald-100 px-1.5 py-0.5 text-xs dark:bg-emerald-950">/home/bfox/brain and eosai prompts/</code>:
                </p>
                <ul className="mb-4 space-y-2 text-sm leading-7 text-zinc-700 dark:text-zinc-300">
                  <li className="flex gap-3">
                    <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-emerald-500" />
                    <span><code className="rounded bg-emerald-100 px-1.5 py-0.5 text-xs dark:bg-emerald-950">brain_startup_prompt.md</code> — the operating manual: what the brain is, how to reach it, the 10 hard rules, the load-bearing pages</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-emerald-500" />
                    <span><code className="rounded bg-emerald-100 px-1.5 py-0.5 text-xs dark:bg-emerald-950">eosai_background_prompt.md</code> — strategic frame: surfaces, harness, skills, methodology, honest gaps</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-emerald-500" />
                    <span><code className="rounded bg-emerald-100 px-1.5 py-0.5 text-xs dark:bg-emerald-950">eosai-framework.md</code> — canonical mirror of the hub page on disk</span>
                  </li>
                </ul>
                <p className="mb-3 text-sm leading-7 text-zinc-700 dark:text-zinc-300">
                  After that, the agent does its own discovery via MCP — reading the hub page,
                  recent timeline entries, the deployment-topology, anything else needed for the
                  task at hand. Brian doesn't re-explain the architecture; the brain does.
                </p>
                <p className="text-sm leading-7 text-zinc-700 dark:text-zinc-300">
                  <span className="font-semibold">When you start in this role, write your own equivalent intro prompt</span> —
                  one paragraph saying who you are, how you like to collaborate, what your hard
                  rules are. Add it next to Brian's. Every future agent that gets briefed sees you
                  too. That's how you become part of the system rather than an outsider operating it.
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Auto-ingest */}
          <div className="mt-8 grid gap-6 lg:grid-cols-2">
            <Card className="rounded-[20px] border border-zinc-200 bg-white dark:border-zinc-800 dark:bg-zinc-900/70">
              <CardContent className="p-6">
                <div className="mb-3 flex items-center gap-3">
                  <div className="rounded-xl bg-sky-100 p-2 text-sky-700 dark:bg-sky-950/40 dark:text-sky-300">
                    <Activity className="h-4 w-4" />
                  </div>
                  <h3 className="text-base font-bold tracking-tight">Auto-ingest worker</h3>
                </div>
                <p className="text-sm leading-7 text-zinc-600 dark:text-zinc-400">
                  Polls 11 active git repos every 5 minutes (ot-sentry, total-recall,
                  forge-runtime, fmptrading, and 7 more). Writes commit summaries as timeline
                  entries on <code className="rounded bg-zinc-100 px-1.5 py-0.5 text-xs dark:bg-zinc-800">projects/&lt;repo&gt;-activity</code> pages.
                  When Brian (or you) commits, the brain's view of the world updates within ~5 min.
                  If timelines stop refreshing — that's the first thing to check.
                </p>
              </CardContent>
            </Card>
            <Card className="rounded-[20px] border border-zinc-200 bg-white dark:border-zinc-800 dark:bg-zinc-900/70">
              <CardContent className="p-6">
                <div className="mb-3 flex items-center gap-3">
                  <div className="rounded-xl bg-amber-100 p-2 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300">
                    <Search className="h-4 w-4" />
                  </div>
                  <h3 className="text-base font-bold tracking-tight">Match query shape to lookup</h3>
                </div>
                <ul className="space-y-1 text-sm leading-7 text-zinc-600 dark:text-zinc-400">
                  <li>"tell me about X" → /v1/recall (synthesis + citations)</li>
                  <li>"topic briefing on X" → /v1/brief (structured summary)</li>
                  <li>"what changed since Y" → /v1/since</li>
                  <li>exact slug → get_page</li>
                  <li>keyword → search (full-text)</li>
                  <li>semantic+keyword RRF → query</li>
                </ul>
              </CardContent>
            </Card>
          </div>

          {/* Load-bearing pages */}
          <div className="mt-8">
            <Card className="rounded-[24px] border-0 bg-zinc-900 text-white shadow-2xl">
              <CardContent className="p-8">
                <div className="mb-4 flex items-center gap-3">
                  <div className="rounded-xl bg-sky-500 p-2 text-white">
                    <FileText className="h-5 w-5" />
                  </div>
                  <h3 className="text-lg font-bold tracking-tight">Load-bearing pages — pre-know these</h3>
                </div>
                <p className="mb-4 text-sm leading-7 text-zinc-300">
                  These ~15 pages show up in most synthesis answers. Glancing at the slug list helps
                  you predict what the brain will surface.
                </p>
                <div className="grid gap-3 sm:grid-cols-2 text-xs leading-6 text-zinc-300">
                  <div>
                    <div className="mb-1 font-bold uppercase tracking-[0.15em] text-zinc-400">Hub + framework</div>
                    <ul className="space-y-0.5">
                      <li><code className="text-sky-300">anchors/eosai-framework</code> — THE hub</li>
                      <li><code className="text-sky-300">harness/&#123;service,reasoning,governance,interaction,cross-cutting&#125;-plane</code></li>
                      <li><code className="text-sky-300">seams/&#123;function-calling,pydantic-contracts,policy-engine,multi-model-router&#125;</code></li>
                      <li><code className="text-sky-300">skills/&#123;...&#125;</code> — full skills inventory</li>
                      <li><code className="text-sky-300">data-sources/&#123;eia,usaspending,bls,noaa,fmp&#125;</code></li>
                    </ul>
                  </div>
                  <div>
                    <div className="mb-1 font-bold uppercase tracking-[0.15em] text-zinc-400">Deployment + product</div>
                    <ul className="space-y-0.5">
                      <li><code className="text-sky-300">ot-sentry-repo/deployment-topology</code></li>
                      <li><code className="text-sky-300">total-recall-repo/deployment-and-architecture</code></li>
                      <li><code className="text-sky-300">anchors/products/&#123;ai-sentry,axiom,forge-runtime,gbrain,fmptrading&#125;</code></li>
                    </ul>
                    <div className="mt-3 mb-1 font-bold uppercase tracking-[0.15em] text-zinc-400">People + open work</div>
                    <ul className="space-y-0.5">
                      <li><code className="text-sky-300">anchors/people/brian-fox</code> + <code className="text-sky-300">brian-operating-mode</code></li>
                      <li><code className="text-sky-300">archive/project_brain_repair_punch_list_2026-05-04</code></li>
                      <li><code className="text-sky-300">parking-lot/production-branch-plan-2026-05-05</code></li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* ── #11 DATA SUBSTRATE ───────────────────────────────── */}
        <section className="py-12 lg:py-16">
          <div className="mb-10">
            <SectionLabel index={8}>Data substrate</SectionLabel>
            <h2 className="text-3xl font-bold tracking-tight">Connectors written once. Inherited by every skill.</h2>
            <p className="mt-3 max-w-2xl text-base leading-7 text-zinc-500 dark:text-zinc-400">
              By owning the connectors at the harness layer, every skill inherits them. The OT
              agent correlates a thermal event against NOAA. The FMP model correlates a position
              against BLS labor pressure. Axiom correlates a site decision against EIA queues.
              This is what makes the corpus compound.
            </p>
          </div>
          <Card className="rounded-[24px] border border-zinc-200 bg-white shadow-md dark:border-zinc-800 dark:bg-zinc-900/80">
            <CardContent className="p-0">
              <div className="divide-y divide-zinc-200 dark:divide-zinc-800">
                {dataConnectors.map((c) => (
                  <div key={c.source} className="grid grid-cols-[10rem_1fr_8rem] items-center gap-4 px-6 py-4 text-sm">
                    <div className="font-semibold text-zinc-800 dark:text-zinc-100">{c.source}</div>
                    <div className="text-zinc-600 dark:text-zinc-400">{c.provides}</div>
                    <div className="text-right">
                      <StatusBadge status={c.status === "Wired" ? "operational" : "planned"} />
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </section>

        {/* ── #12 VISION PLATFORM METHODOLOGY ─────────────────── */}
        <section className="py-12 lg:py-16">
          <div className="mb-10">
            <SectionLabel index={9}>Cross-skill methodology</SectionLabel>
            <h2 className="text-3xl font-bold tracking-tight">The Vision Platform 5-layer pattern.</h2>
            <p className="mt-3 max-w-2xl text-base leading-7 text-zinc-500 dark:text-zinc-400">
              Several existing skills already exercise pieces of this. Phase-1 abstraction is meant
              to formalize it as a portable platform — clinical / structural-biology / disaster /
              infrastructure / agriculture / industrial.
            </p>
          </div>
          <div className="grid gap-4 lg:grid-cols-2">
            {[
              { num: "L1", title: "Multi-scale structural pattern detection", body: "Decompose noisy sequential/spatial data at 3 sensitivities. Genuine patterns appear at all scales; noise does not. (FMP: ZigZag at 0.18 / 0.12 / 0.08 + Wave Quality Score.)" },
              { num: "L2", title: "Quantitative quality filtering", body: "Cheap pre-filter concentrates expensive AI on high-probability candidates. (FMP: Yartseva 7-factor → Rule of 40 (2-variable) matched it. Logical soundness matters, not factor count.)" },
              { num: "L3", title: "Multimodal AI classification with practitioner-calibrated ground truth", body: "Vision LLM scoring against annotated expert labels. (FMP: 83% → 86% overall, 93% NOT_A_BUY precision after negative-example engineering.)" },
              { num: "L4", title: "YOLO-style geometric pattern detection", body: "Pretrained deterministic detector with a different error profile from the Vision LLM. (FMP: YOLOv8 on chart geometry at 67%.)" },
              { num: "L5", title: "Validated ensemble fusion", body: "Weighted blend (70% Vision / 30% YOLO), validated via 240-combo grid search." },
            ].map((l) => (
              <Card key={l.num} className="rounded-[20px] border border-zinc-200 bg-white dark:border-zinc-800 dark:bg-zinc-900/70">
                <CardContent className="p-6">
                  <div className="mb-2 flex items-center gap-3">
                    <div className="rounded-lg bg-violet-100 px-2.5 py-1 text-xs font-bold tracking-wider text-violet-700 dark:bg-violet-950/40 dark:text-violet-300">{l.num}</div>
                    <h3 className="text-base font-bold tracking-tight">{l.title}</h3>
                  </div>
                  <p className="text-sm leading-7 text-zinc-600 dark:text-zinc-400">{l.body}</p>
                </CardContent>
              </Card>
            ))}
            <Card className="rounded-[20px] border-2 border-emerald-200 bg-emerald-50/40 dark:border-emerald-900 dark:bg-emerald-950/20">
              <CardContent className="p-6">
                <div className="mb-2 text-xs font-bold uppercase tracking-[0.15em] text-emerald-700 dark:text-emerald-300">Two practices that transfer unchanged to any domain</div>
                <p className="mb-2 text-sm leading-7 text-zinc-700 dark:text-zinc-300">
                  <span className="font-semibold">Negative-example engineering</span> — show the model
                  what the wrong answer looks like with the same specificity as the right answer.
                  NOT_A_BUY accuracy 50% → 93% from this alone.
                </p>
                <p className="text-sm leading-7 text-zinc-700 dark:text-zinc-300">
                  <span className="font-semibold">Silent-failure detection</span> — pipeline integrity
                  validation. (9 charts in fmptrading were returning 400 API errors due to WebP
                  encoding mis-detection, silently corrupting the dataset until caught.)
                </p>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* ── #13 DURABLE EDGES ────────────────────────────────── */}
        <section className="py-12 lg:py-16">
          <div className="mb-10">
            <SectionLabel index={10}>Durable edges</SectionLabel>
            <h2 className="text-3xl font-bold tracking-tight">What's actually hard to copy.</h2>
          </div>
          <div className="grid gap-4 lg:grid-cols-2">
            {durableEdges.map((e) => (
              <Card key={e.name} className="rounded-[20px] border border-zinc-200 bg-white dark:border-zinc-800 dark:bg-zinc-900/70">
                <CardContent className="p-6">
                  <h3 className="mb-2 text-base font-bold tracking-tight">{e.name}</h3>
                  <p className="text-sm leading-7 text-zinc-600 dark:text-zinc-400">{e.why}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        {/* ── #14 HONEST GAPS ──────────────────────────────────── */}
        <section className="py-12 lg:py-16">
          <div className="mb-10">
            <SectionLabel index={11}>Honest gaps</SectionLabel>
            <h2 className="text-3xl font-bold tracking-tight">What's not done yet.</h2>
            <p className="mt-3 max-w-2xl text-base leading-7 text-zinc-500 dark:text-zinc-400">
              Brian values "I don't have visibility into X" over confident fabrication. So this list
              is honest about what isn't shipped, isn't tested, or isn't tracked. Don't promise
              these as done.
            </p>
          </div>
          <div className="grid gap-4 lg:grid-cols-2">
            {honestGaps.map((g) => (
              <Card key={g.name} className="rounded-[20px] border border-amber-200 bg-amber-50/40 dark:border-amber-900 dark:bg-amber-950/20">
                <CardContent className="p-6">
                  <div className="mb-2 flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                    <h3 className="text-base font-bold tracking-tight">{g.name}</h3>
                  </div>
                  <p className="text-sm leading-7 text-zinc-700 dark:text-zinc-300">{g.detail}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        {/* ── #15 OPERATING RULES ──────────────────────────────── */}
        <section className="py-12 lg:py-16">
          <div className="mb-10">
            <SectionLabel index={12}>Operating rules</SectionLabel>
            <h2 className="text-3xl font-bold tracking-tight">Brian's ten hard-learned rules. Each one came from a specific incident.</h2>
            <p className="mt-3 max-w-2xl text-base leading-7 text-zinc-500 dark:text-zinc-400">
              You should not violate these even if you think you have a good reason. If you think
              you have a reason, the answer is to put it on the table with Brian — not to override.
            </p>
          </div>
          <div className="space-y-3">
            {operatingRules.map((r, i) => (
              <Card key={i} className="rounded-[18px] border border-zinc-200 bg-white dark:border-zinc-800 dark:bg-zinc-900/70">
                <CardContent className="p-5">
                  <div className="grid gap-3 lg:grid-cols-[3rem_1fr]">
                    <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-zinc-900 text-sm font-bold text-white dark:bg-zinc-100 dark:text-zinc-900">{i + 1}</div>
                    <div>
                      <p className="mb-1 text-base font-semibold leading-7 text-zinc-800 dark:text-zinc-100">{r.rule}</p>
                      <p className="text-sm leading-7 text-zinc-600 dark:text-zinc-400">{r.why}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        {/* ── #16 YOUR FIRST 30 DAYS ───────────────────────────── */}
        <section className="py-12 lg:py-16">
          <div className="mb-10">
            <SectionLabel index={13}>Your first 30 days</SectionLabel>
            <h2 className="text-3xl font-bold tracking-tight">Concrete. Sequenced. Real.</h2>
            <p className="mt-3 max-w-2xl text-base leading-7 text-zinc-500 dark:text-zinc-400">
              You don't need to read the codebase to ramp. You need to read the brain. Then pair
              once. Then make yourself part of the substrate.
            </p>
          </div>
          <div className="grid gap-5 lg:grid-cols-2">
            {first30Days.map((w) => (
              <Card key={w.week} className="rounded-[20px] border-2 border-zinc-100 bg-white shadow-md dark:border-zinc-800 dark:bg-zinc-900/80">
                <CardContent className="p-6">
                  <div className="mb-3 flex items-baseline gap-3">
                    <div className="text-xs font-bold uppercase tracking-[0.18em] text-zinc-400">{w.week}</div>
                    <div className="text-base font-bold tracking-tight">{w.title}</div>
                  </div>
                  <ul className="space-y-2">
                    {w.items.map((it, i) => (
                      <li key={i} className="flex gap-2 text-sm leading-7 text-zinc-700 dark:text-zinc-300">
                        <CheckCircle2 className="mt-1 h-4 w-4 shrink-0 text-emerald-500" />
                        <span>{it}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        {/* ── #17 CLOSING ──────────────────────────────────────── */}
        <section className="pt-12 lg:pt-16">
          <Card className="rounded-[32px] border-0 bg-gradient-to-br from-zinc-900 via-zinc-900 to-zinc-800 text-white shadow-2xl">
            <CardContent className="p-10 lg:p-14">
              <div className="mb-6 text-xs font-bold uppercase tracking-[0.2em] text-zinc-400">Closing</div>
              <h2 className="text-3xl font-bold leading-tight tracking-tight lg:text-4xl">
                The shape of what eosai actually is.
              </h2>
              <p className="mt-6 text-lg leading-9 text-zinc-300">
                eosai.ai is the enterprise AI plane. The harness handles every cross-cutting
                concern that every AI use case needs and that every team rebuilds badly. The
                skills handle the domain-deep intelligence that no harness can possibly anticipate.
                Function-calling, Pydantic contracts, the policy engine, and the multi-model
                router are the four seams that hold them together.
              </p>
              <p className="mt-5 text-lg leading-9 text-zinc-300">
                dev.eosai.ai is the live proof that this is not a slide — it is running. Axiom is
                the first fully-expressed skill, with multi-stakeholder process flows already in
                motion. The OT, Data Center, Vision, FMP, back-test, walk-forward, six-NLP-agent
                risk framework, and AHP decision tool are the breadth proof — heterogeneous skills,
                identical harness. EIA, USAspending, BLS, NOAA, and FMP feeds are the data
                substrate that every future skill inherits the moment it plugs in.
              </p>
              <p className="mt-5 text-lg leading-9 text-zinc-300">
                And the brain — Total Recall + gbrain — is what makes it possible for AI agents to
                pick this work up where the last session left off. Brian uses it as a tool for a
                digital user. You will too. That's the multiplier you're inheriting.
              </p>
              <div className="mt-10 rounded-2xl border border-zinc-700 bg-zinc-950/60 p-6">
                <p className="text-sm leading-7 text-zinc-300">
                  <span className="font-semibold text-white">The horizontal infusion claim is not aspirational.</span> It is
                  what the architecture produces by construction. The job is to keep it that way —
                  thin where it should be thin, fat where it should be fat, governed everywhere,
                  and never re-implementing what the harness already provides.
                </p>
                <p className="mt-4 text-sm font-semibold text-zinc-400">— Welcome aboard.</p>
              </div>
            </CardContent>
          </Card>
        </section>

      </div>
    </div>
  );
}
